import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import type { IdentityDataNodeProps } from '@/playbook-builder/playbook-builder.types';
import type { ApplyEditMutation } from '../../../base-node';
import type { FormIdentityData } from '../../../collect-data-node/components/person/person.types';
import { DEFAULT_CHALLENGE_KIND } from '../../../shared/verified-prefill-challenge-kind';
import { toOutputMappings } from '../../../shared/verified-prefill-outputs';
import createIdentityDataPayload from '../../utils/create-payload';
import { collectsData, isDataOptional } from '../../utils/data-collection-utils';
import { ConfirmationSection } from '../confirmation-section';
import { DisabledCdosSection } from '../disabled-cdos-section';
import InformationForm from '../information-form';
import InformationSection from '../information-section';
import ResidencyForm from '../residency-form';
import ResidencyView from '../residency-view';
import { VerifiedPrefillSection } from '../verified-prefill-section';
export type IdentityContentProps = {
  node: IdentityDataNodeProps;
  mutation: ApplyEditMutation;
  canEdit: boolean;
  isFirmEmployee: boolean;
};
const IdentityContent = t0 => {
  const $ = _c(59);
  const {
    node,
    mutation,
    canEdit,
    isFirmEmployee
  } = t0;
  const collects = collectsData(node.data);
  const optional = isDataOptional(node.data);
  const {
    toggle
  } = useDirty();
  const t1 = node.data.residentialAddressCountries;
  const t2 = collects("email");
  const t3 = collects("phone_number");
  const t4 = collects("us_legal_status");
  const t5 = collects("us_tax_id") || collects("ssn9") || collects("ssn4");
  const t6 = collects("us_tax_id") ? "us_tax_id" : collects("ssn9") ? "ssn9" : "ssn4";
  const t7 = optional("ssn4") || optional("ssn9") || optional("us_tax_id");
  let t8;
  if ($[0] !== t5 || $[1] !== t6 || $[2] !== t7) {
    t8 = {
      collect: t5,
      kind: t6,
      optional: t7
    };
    $[0] = t5;
    $[1] = t6;
    $[2] = t7;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  const t9 = node.data.enabledVerifiedPrefill;
  const t10 = node.data.verifiedPrefillSettings?.challengeKind ?? DEFAULT_CHALLENGE_KIND;
  let t11;
  if ($[4] !== node.data.verifiedPrefillSettings?.skipIfPopulated) {
    t11 = node.data.verifiedPrefillSettings?.skipIfPopulated?.includes("ssn9") ?? true;
    $[4] = node.data.verifiedPrefillSettings?.skipIfPopulated;
    $[5] = t11;
  } else {
    t11 = $[5];
  }
  let t12;
  if ($[6] !== node.data.verifiedPrefillSettings?.skipIfPopulated) {
    t12 = node.data.verifiedPrefillSettings?.skipIfPopulated?.includes("full_address") ?? true;
    $[6] = node.data.verifiedPrefillSettings?.skipIfPopulated;
    $[7] = t12;
  } else {
    t12 = $[7];
  }
  const t13 = node.data.verifiedPrefillSettings?.outputs;
  let t14;
  if ($[8] !== t13) {
    t14 = toOutputMappings(t13);
    $[8] = t13;
    $[9] = t14;
  } else {
    t14 = $[9];
  }
  let t15;
  if ($[10] !== node.data.confirmationMode || $[11] !== node.data.disabledCdos || $[12] !== node.data.enabledVerifiedPrefill || $[13] !== node.data.residentialAddressCountries || $[14] !== node.data.useCompactConfirm || $[15] !== t10 || $[16] !== t11 || $[17] !== t12 || $[18] !== t14 || $[19] !== t2 || $[20] !== t3 || $[21] !== t4 || $[22] !== t8) {
    t15 = {
      defaultValues: {
        residentialAddressCountries: t1,
        email: t2,
        phoneNumber: t3,
        usLegalStatus: t4,
        ssn: t8,
        enabledVerifiedPrefill: t9,
        challengeKind: t10,
        skipIfSsnPopulated: t11,
        skipIfFullAddressPopulated: t12,
        outputs: t14,
        useCompactConfirm: node.data.useCompactConfirm,
        disabledCdos: node.data.disabledCdos,
        confirmationMode: node.data.confirmationMode
      }
    };
    $[10] = node.data.confirmationMode;
    $[11] = node.data.disabledCdos;
    $[12] = node.data.enabledVerifiedPrefill;
    $[13] = node.data.residentialAddressCountries;
    $[14] = node.data.useCompactConfirm;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
    $[18] = t14;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t8;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  const methods = useForm(t15);
  let t16;
  if ($[24] !== methods.formState.isDirty || $[25] !== toggle) {
    t16 = () => {
      toggle("identity-data-form", methods.formState.isDirty);
    };
    $[24] = methods.formState.isDirty;
    $[25] = toggle;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== methods.formState) {
    t17 = [methods.formState];
    $[27] = methods.formState;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  useEffect(t16, t17);
  let t18;
  if ($[29] !== mutation || $[30] !== node) {
    t18 = async formData => {
      ;
      try {
        await mutation.mutateAsync({
          kind: "update_node",
          nodeData: createIdentityDataPayload(node, formData),
          nodeId: node.id
        });
      } catch (t19) {
        const error = t19;
        console.error("Error updating playbook session:", error);
      }
    };
    $[29] = mutation;
    $[30] = node;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  const handleSubmit = t18;
  let t19;
  if ($[32] !== handleSubmit || $[33] !== methods) {
    t19 = methods.handleSubmit(handleSubmit);
    $[32] = handleSubmit;
    $[33] = methods;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  let t21;
  if ($[35] !== canEdit || $[36] !== node) {
    t20 = canEdit ? <InformationForm node={node} /> : <InformationSection node={node} />;
    t21 = canEdit ? <ResidencyForm /> : <ResidencyView node={node} />;
    $[35] = canEdit;
    $[36] = node;
    $[37] = t20;
    $[38] = t21;
  } else {
    t20 = $[37];
    t21 = $[38];
  }
  let t22;
  let t23;
  if ($[39] !== canEdit || $[40] !== isFirmEmployee) {
    t22 = <VerifiedPrefillSection canEdit={canEdit} isFirmEmployee={isFirmEmployee} />;
    t23 = <ConfirmationSection canEdit={canEdit} isFirmEmployee={isFirmEmployee} />;
    $[39] = canEdit;
    $[40] = isFirmEmployee;
    $[41] = t22;
    $[42] = t23;
  } else {
    t22 = $[41];
    t23 = $[42];
  }
  let t24;
  if ($[43] !== canEdit || $[44] !== isFirmEmployee || $[45] !== node) {
    t24 = <DisabledCdosSection node={node} canEdit={canEdit} isFirmEmployee={isFirmEmployee} />;
    $[43] = canEdit;
    $[44] = isFirmEmployee;
    $[45] = node;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== t20 || $[48] !== t21 || $[49] !== t22 || $[50] !== t23 || $[51] !== t24) {
    t25 = <div className="flex flex-col gap-4 pb-4">{t20}{t21}{t22}{t23}{t24}</div>;
    $[47] = t20;
    $[48] = t21;
    $[49] = t22;
    $[50] = t23;
    $[51] = t24;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== t19 || $[54] !== t25) {
    t26 = <form id="identity-data-form" onSubmit={t19}>{t25}</form>;
    $[53] = t19;
    $[54] = t25;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== methods || $[57] !== t26) {
    t27 = <FormProvider {...methods}>{t26}</FormProvider>;
    $[56] = methods;
    $[57] = t26;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  return t27;
};
export default IdentityContent;